import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Users, Calendar } from "lucide-react";
import api from "../utils/api";
import { toast } from "sonner";
import useThemeStore from "../store/themeStore";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

import { Textarea } from "../components/ui/textarea";

import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Plus } from "lucide-react";

const GroupsList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    budget: "",
    gift_date: "",
    theme: "",
  });

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await api.get("/groups");

      setGroups(response.data);
    } catch (error) {
      toast.error(t("error"), {
        description: error.response?.data?.detail || "Failed to load groups",
      });
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.gift_date) {
      toast.error(t("error"), { description: "Name and date are required" });
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/groups", {
        name: formData.name,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        gift_date: formData.gift_date,
        theme: formData.theme || null,
      });

      toast.success(t("success"), {
        description: "Group created successfully!",
      });
      navigate(`/group/${response.data.id}`);
    } catch (error) {
      toast.error(t("error"), {
        description: error.response?.data?.detail || "Failed to create group",
      });
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">{t("loading")}</div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${
        theme === "dark"
          ? "bg-gray-900"
          : "bg-gradient-to-br from-red-50 via-pink-50 to-blue-50"
      } py-8 px-4`}
    >
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-row justify-between ">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
              {t("myGroups")}
            </h1>
            <Button
              data-testid="create-group-button"
              variant="outline"
              className=""
              onClick={() => setShowCreateGroupDialog(true)}
            >
              <Plus />
            </Button>
          </div>
          {/* <Button
              data-testid="join-group-button"
              size="lg"
              variant="outline"
              className="px-8 py-6 text-lg rounded-full shadow-lg"
              onClick={() => setShowJoinDialog(true)}
            >
              <Users className="mr-2 h-5 w-5" />
              {t("joinGroup")}
            </Button> */}

          {groups.length === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                You haven't joined any groups yet
              </p>

              <Button
                data-testid="create-first-group-button"
                onClick={() => navigate("/create-group")}
                className="bg-rose-600 hover:bg-rose-700"
              >
                {t("createGroup")}
              </Button>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groups.map((group) => (
                <motion.div
                  key={group.id}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card
                    data-testid={`group-card-${group.id}`}
                    className="p-6 cursor-pointer hover:shadow-lg"
                    onClick={() => navigate(`/group/${group.id}`)}
                  >
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {group.name}
                    </h3>
                    {group.theme && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {group.theme}
                      </p>
                    )}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <Users className="h-4 w-4" />
                        {group.participants.length} {t("participants")}
                      </div>
                      <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <Calendar className="h-4 w-4" />
                        {new Date(group.gift_date).toLocaleDateString()}
                      </div>
                    </div>
                    {group.draw_completed && (
                      <div className="mt-4 inline-block bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100 px-3 py-1 rounded-full text-xs font-semibold">
                        Draw Completed
                      </div>
                    )}
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
      <Dialog
        open={showCreateGroupDialog}
        onOpenChange={setShowCreateGroupDialog}
      >
        <DialogContent data-testid="create-group-dialog">
          <DialogHeader>
            <DialogTitle>{t("createGroup")}</DialogTitle>
            <DialogDescription>
              {t("createGroupDescription") ||
                "Create a new gift exchange group."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">{t("groupName")}</Label>
              <Input
                data-testid="group-name-input"
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Family Secret Santa 2025"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="budget">{t("budget")}</Label>
              <Input
                data-testid="budget-input"
                id="budget"
                type="number"
                min="0"
                step="0.01"
                value={formData.budget}
                onChange={(e) =>
                  setFormData({ ...formData, budget: e.target.value })
                }
                placeholder="50.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="gift_date">{t("giftDate")}</Label>
              <Input
                data-testid="gift-date-input"
                id="gift_date"
                type="date"
                value={formData.gift_date}
                onChange={(e) =>
                  setFormData({ ...formData, gift_date: e.target.value })
                }
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="theme">{t("theme")}</Label>
              <Textarea
                data-testid="theme-input"
                id="theme"
                value={formData.theme}
                onChange={(e) =>
                  setFormData({ ...formData, theme: e.target.value })
                }
                placeholder="Christmas, Winter Wonderland, etc."
                rows={3}
              />
            </div>

            <div className="flex gap-4">
              <Button
                data-testid="cancel-button"
                type="button"
                variant="outline"
                // onClick={() => navigate("/")}
                onClick={() => setShowCreateGroupDialog(false)}
                className="flex-1"
              >
                {t("cancel")}
              </Button>
              <Button
                data-testid="create-button"
                type="submit"
                disabled={loading}
                className="flex-1 bg-rose-600 hover:bg-rose-700"
              >
                {loading ? t("loading") : t("create")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupsList;
